import { apiClient, setAuthHeader } from '../config/api';
// Import central types
import { 
  Note, 
  // NoteFormData, // Currently unused
  UserStats, 
  NoteImpact, 
  NotesFilter 
} from '../types/interfaces';

// Note interfaces
// Removed local Note interface definition
// Removed local NoteFormData interface definition

export interface FilterOptions {
  status?: string;
  category?: string;
  tag?: string;
  location?: string;
  sortBy?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// Interface for note drafts
export interface NoteDraft {
  _id?: string;
  noteId?: string;
  note: string;
  contributorInfo: {
    firstName: string;
    lastName: string;
    aliasName?: string;
    email: string;
  };
  location: {
    zipcode: string;
    city: string;
    state: string;
  };
  categories?: string[];
  tags?: string[];
  lastSaved: string;
}

// Mocked draft data
const draftsMockData: NoteDraft[] = [];

// Interface for backend API response
interface NotesApiResponse {
  success: boolean;
  notes: Note[];
  total: number;
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// API Functions

/**
 * Fetch all notes with optional filtering
 */
export const fetchNotes = async (filters: FilterOptions = {}): Promise<{ notes: Note[], total: number, pagination?: any }> => {
  try {
    // Set authentication header if user is logged in (for role-based data access)
    setAuthHeader(apiClient);
    
    const response = await apiClient.get('/notes', { params: filters });
    
    // Backend returns: { success: true, notes: Note[], total: number, pagination: {...} }
    const data = response.data as NotesApiResponse;
    
    return {
      notes: data.notes || [],
      total: data.total || data.pagination?.total || 0,
      pagination: data.pagination
    };
  } catch (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }
};

/**
 * Fetch a single note by ID
 */
export const fetchNote = async (id: string): Promise<Note> => {
  try {
    // Set authentication header if user is logged in (for role-based data access)
    setAuthHeader(apiClient);
    
    const response = await apiClient.get(`/notes/${id}`);
    return (response.data as { success: boolean; data: Note }).data;
  } catch (error) {
    console.error(`Error fetching note ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new note
 */
export const createNote = async (noteData: Partial<Note>): Promise<Note> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.post('/notes', noteData);
    return (response.data as { success: boolean; data: Note }).data;
  } catch (error) {
    console.error('Error creating note:', error);
    throw error;
  }
};

/**
 * Create a new public note (unauthenticated)
 */
export const createPublicNote = async (noteData: any): Promise<Note> => {
  try {
    // No authentication needed for public notes
    const response = await apiClient.post('/public/notes', noteData);
    return (response.data as { success: boolean; data: Note }).data;
  } catch (error) {
    console.error('Error creating public note:', error);
    throw error;
  }
};

/**
 * Update an existing note
 */
export const updateNote = async (id: string, noteData: Partial<Note>): Promise<Note> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.put(`/notes/${id}`, noteData);
    return (response.data as { success: boolean; data: Note }).data;
  } catch (error) {
    console.error(`Error updating note ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a note
 */
export const deleteNote = async (id: string): Promise<{ success: boolean }> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.delete(`/notes/${id}`);
    return response.data as { success: boolean };
  } catch (error) {
    console.error(`Error deleting note ${id}:`, error);
    throw error;
  }
};

/**
 * Heart/like a note
 */
export const heartNote = async (id: string): Promise<{ success: boolean }> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.post(`/contributor/notes/${id}/heart`);
    return response.data as { success: boolean };
  } catch (error) {
    console.error(`Error hearting note ${id}:`, error);
    throw error;
  }
};

/**
 * Flag a note
 */
export const flagNote = async (id: string, reason: string): Promise<{ success: boolean }> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.post(`/contributor/notes/${id}/flag`, { reason });
    return response.data as { success: boolean };
  } catch (error) {
    console.error(`Error flagging note ${id}:`, error);
    throw error;
  }
};

/**
 * Get related notes for a specific note
 */
export const getRelatedNotes = async (id: string): Promise<Note[]> => {
  try {
    const response = await apiClient.get(`/notes/${id}/related`);
    return (response.data as { relatedNotes: Note[] }).relatedNotes;
  } catch (error) {
    console.error(`Error fetching related notes for ${id}:`, error);
    throw error;
  }
};

/**
 * Get user's notes
 */
export const getUserNotes = async (filters?: NotesFilter): Promise<Note[]> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get('/notes/my-notes', { params: filters });
    return (response.data as { notes: Note[] }).notes;
  } catch (error) {
    console.error('Error fetching user notes:', error);
    throw error;
  }
};

/**
 * Get user's own notes (alias for getUserNotes)
 */
export const getMyNotes = async (filters?: NotesFilter): Promise<Note[]> => {
  return getUserNotes(filters);
};

/**
 * Get user statistics
 */
export const getUserStats = async (): Promise<UserStats> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get('/notes/user-stats');
    return response.data as UserStats;
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
};

/**
 * Get note impact metrics
 */
export const getNoteImpact = async (id: string): Promise<NoteImpact> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get(`/contributor/notes/${id}/impact`);
    return (response.data as { impact: NoteImpact }).impact;
  } catch (error) {
    console.error(`Error fetching impact for note ${id}:`, error);
    throw error;
  }
};

/**
 * Save a draft of a note (create new or update existing)
 * @param draft Draft data to save
 * @returns The saved draft object
 */
export const saveDraft = async (draft: Partial<NoteDraft>): Promise<NoteDraft> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // If this is an update to an existing draft
  if (draft._id) {
    const index = draftsMockData.findIndex(d => d._id === draft._id);
    if (index >= 0) {
      const updatedDraft: NoteDraft = {
        ...draftsMockData[index],
        ...draft,
        lastSaved: new Date().toISOString()
      };
      draftsMockData[index] = updatedDraft;
      return updatedDraft;
    }
  }
  
  // Create a new draft
  const newDraft: NoteDraft = {
    _id: 'draft_' + Math.random().toString(36).substr(2, 9),
    note: draft.note || '',
    contributorInfo: draft.contributorInfo || { firstName: '', lastName: '', email: '' },
    location: draft.location || { zipcode: '', city: '', state: ''},
    categories: draft.categories || [],
    tags: draft.tags || [],
    lastSaved: new Date().toISOString(),
    noteId: draft.noteId,
    ...(draft as Partial<NoteDraft>)
  };
  
  draftsMockData.push(newDraft);
  return newDraft;
};

/**
 * Get all drafts for the current user
 * @returns Array of draft objects
 */
export const getNoteDrafts = async (): Promise<NoteDraft[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  return [...draftsMockData];
};

/**
 * Get a specific draft by ID
 * @param id Draft ID
 * @returns The draft object or null if not found
 */
export const getNoteDraft = async (id: string): Promise<NoteDraft | null> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const draft = draftsMockData.find(d => d._id === id);
  return draft || null;
};

/**
 * Delete a draft by ID
 * @param id Draft ID to delete
 * @returns Success flag
 */
export const deleteNoteDraft = async (id: string): Promise<boolean> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const index = draftsMockData.findIndex(d => d._id === id);
  if (index >= 0) {
    draftsMockData.splice(index, 1);
    return true;
  }
  return false;
};

/**
 * Fetch notes available for adding to a display
 * This will get approved notes that can be added to displays
 */
export const getNotesForDisplay = async (displayId?: string, filters?: FilterOptions): Promise<Note[]> => {
  try {
    const params = {
      ...filters,
      status: 'approved',
      excludeDisplay: displayId, // exclude notes already in this display
      limit: filters?.limit || 50
    };
    
    const response = await apiClient.get('/notes/available-for-display', { params });
    return (response.data as { notes: Note[] }).notes;
  } catch (error) {
    console.error('Error fetching notes for display:', error);
    throw error;
  }
};

/**
 * Toggle heart on a note
 * @param {string} noteId - ID of the note to heart/unheart
 * @returns {Promise<{noteId: string, hearted: boolean, newHeartCount: number}>} Updated heart status
 */
export const toggleHeartNote = async (noteId: string): Promise<{noteId: string, hearted: boolean, newHeartCount: number}> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.post(`/contributor/notes/${noteId}/heart`);
    return response.data as {noteId: string, hearted: boolean, newHeartCount: number};
  } catch (error) {
    console.error('Error toggling heart on note:', error);
    throw error;
  }
};

/**
 * Fetch public note content by its publicId (UUID)
 * This endpoint returns a sanitized version of the note for public display.
 */
export const fetchPublicNoteContentByPublicId = async (publicId: string): Promise<Note> => {
  try {
    const response = await apiClient.get(`/public/notes/${publicId}/content`);
    return (response.data as { data: Note }).data;
  } catch (error) {
    console.error(`Error fetching public note content by publicId ${publicId}:`, error);
    throw error;
  }
};

/**
 * Fetch owned note content by note ID
 * This endpoint returns full note content for notes owned by the current user.
 */
export const fetchOwnedNoteContent = async (id: string): Promise<Note> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get(`/contributor/notes/${id}/content`);
    return (response.data as { note: Note }).note;
  } catch (error) {
    console.error(`Error fetching owned note content for ${id}:`, error);
    throw error;
  }
};

/**
 * Fetch random public notes for display
 * This endpoint returns a small collection of random approved notes (no authentication required)
 */
export const fetchRandomNotes = async (limit: number = 5): Promise<Note[]> => {
  try {
    // No authentication needed for public random notes
    const response = await apiClient.get('/public/notes/random', {
      params: { limit: Math.min(limit, 10) } // API max is 10
    });
    return (response.data as { success: boolean; data: { notes: Note[] } }).data.notes;
  } catch (error) {
    console.error('Error fetching random notes:', error);
    throw error;
  }
};

/**
 * Claim notes for the authenticated user
 * Links anonymous notes to the user's account
 */
export interface ClaimResult {
  claimed: number;
  noteIds: string[];
}

export const claimNotes = async (noteId?: string): Promise<ClaimResult> => {
  try {
    setAuthHeader(apiClient);
    const response = await apiClient.post('/notes/claim', { noteId });
    return response.data as ClaimResult;
  } catch (error) {
    console.error('Error claiming notes:', error);
    throw error;
  }
};

/**
 * Service for notes-related API calls
 */
export const notesService = {
  /**
   * Fetch all notes with optional filters
   */
  async fetchNotes(filters?: NotesFilter): Promise<Note[]> {
    try {
      // Set authentication header if user is logged in (for role-based data access)
      setAuthHeader(apiClient);
      
      const response = await apiClient.get('/notes', { params: filters });
      return (response.data as { notes: Note[] }).notes;
    } catch (error) {
      console.error('Error in fetchNotes:', error);
      throw error;
    }
  },

  /**
   * Fetch a single note by ID
   */
  async fetchNote(id: string): Promise<Note> {
    try {
      // Set authentication header if user is logged in (for role-based data access)
      setAuthHeader(apiClient);
      
      const response = await apiClient.get(`/notes/${id}`);
      return (response.data as { success: boolean; data: Note }).data;
    } catch (error) {
      console.error(`Error fetching note ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new note
   */
  async createNote(noteData: Partial<Note>): Promise<Note> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.post('/notes', noteData);
      return (response.data as { success: boolean; data: Note }).data;
    } catch (error) {
      console.error('Error in createNote:', error);
      throw error;
    }
  },

  /**
   * Update an existing note
   */
  async updateNote(id: string, noteData: Partial<Note>): Promise<Note> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.put(`/notes/${id}`, noteData);
      return (response.data as { success: boolean; data: Note }).data;
    } catch (error) {
      console.error(`Error updating note ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a note
   */
  async deleteNote(id: string): Promise<{ success: boolean }> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.delete(`/notes/${id}`);
      return response.data as { success: boolean };
    } catch (error) {
      console.error(`Error deleting note ${id}:`, error);
      throw error;
    }
  },

  /**
   * Heart/like a note
   */
  async heartNote(id: string): Promise<{ success: boolean }> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.post(`/contributor/notes/${id}/heart`);
      return response.data as { success: boolean };
    } catch (error) {
      console.error(`Error hearting note ${id}:`, error);
      throw error;
    }
  },

  /**
   * Flag a note
   */
  async flagNote(id: string, reason: string): Promise<{ success: boolean }> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.post(`/contributor/notes/${id}/flag`, { reason });
      return response.data as { success: boolean };
    } catch (error) {
      console.error(`Error flagging note ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get related notes for a specific note
   */
  async getRelatedNotes(id: string): Promise<Note[]> {
    try {
      const response = await apiClient.get(`/notes/${id}/related`);
      return (response.data as { relatedNotes: Note[] }).relatedNotes;
    } catch (error) {
      console.error(`Error in getRelatedNotes for ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get user's notes
   */
  async getUserNotes(filters?: NotesFilter): Promise<Note[]> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.get('/notes/my-notes', { params: filters });
      return (response.data as { notes: Note[] }).notes;
    } catch (error) {
      console.error('Error in getUserNotes:', error);
      throw error;
    }
  },

  /**
   * Get user's own notes (alias for getUserNotes)
   */
  async getMyNotes(filters?: NotesFilter): Promise<Note[]> {
    try {
      return this.getUserNotes(filters);
    } catch (error) {
      console.error('Error in getMyNotes:', error);
      throw error;
    }
  },

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<UserStats> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.get('/notes/user-stats');
      return response.data as UserStats;
    } catch (error) {
      console.error('Error in getUserStats:', error);
      throw error;
    }
  },

  /**
   * Get note impact metrics
   */
  async getNoteImpact(id: string): Promise<NoteImpact> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.get(`/contributor/notes/${id}/impact`);
      return (response.data as { impact: NoteImpact }).impact;
    } catch (error) {
      console.error(`Error in getNoteImpact for ${id}:`, error);
      throw error;
    }
  },

  /**
   * Fetch notes available for adding to a display
   * This will get approved notes that can be added to displays
   */
  async getNotesForDisplay(displayId?: string, filters?: FilterOptions): Promise<Note[]> {
    try {
      const params = {
        ...filters,
        status: 'approved',
        excludeDisplay: displayId, // exclude notes already in this display
        limit: filters?.limit || 50
      };
      
      const response = await apiClient.get('/notes/available-for-display', { params });
      return (response.data as { notes: Note[] }).notes;
    } catch (error) {
      console.error('Error fetching notes for display:', error);
      throw error;
    }
  },

  /**
   * Fetch public note content by its publicId (UUID)
   * This endpoint returns a sanitized version of the note for public display.
   */
  async fetchPublicNoteContentByPublicId(publicId: string): Promise<Note> {
    try {
      const response = await apiClient.get(`/public/notes/${publicId}/content`);
      return (response.data as { data: Note }).data;
    } catch (error) {
      console.error(`Error fetching public note content by publicId ${publicId}:`, error);
      throw error;
    }
  },

  /**
   * Toggle heart on a note
   * @param {string} noteId - ID of the note to heart/unheart
   * @returns {Promise<{noteId: string, hearted: boolean, newHeartCount: number}>} Updated heart status
   */
  async toggleHeartNote(noteId: string): Promise<{noteId: string, hearted: boolean, newHeartCount: number}> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.post(`/contributor/notes/${noteId}/heart`);
      return response.data as {noteId: string, hearted: boolean, newHeartCount: number};
    } catch (error) {
      console.error('Error toggling heart on note:', error);
      throw error;
    }
  },

  /**
   * Fetch owned note content by note ID
   * This endpoint returns full note content for notes owned by the current user.
   */
  async fetchOwnedNoteContent(id: string): Promise<Note> {
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      const response = await apiClient.get(`/contributor/notes/${id}/content`);
      return (response.data as { note: Note }).note;
    } catch (error) {
      console.error(`Error fetching owned note content for ${id}:`, error);
      throw error;
    }
  },

  /**
   * Fetch random public notes for display
   * This endpoint returns a small collection of random approved notes (no authentication required)
   */
  async fetchRandomNotes(limit: number = 5): Promise<Note[]> {
    try {
      // No authentication needed for public random notes
      const response = await apiClient.get('/public/notes/random', {
        params: { limit: Math.min(limit, 10) } // API max is 10
      });
      return (response.data as { success: boolean; data: { notes: Note[] } }).data.notes;
    } catch (error) {
      console.error('Error fetching random notes:', error);
      throw error;
    }
  },
}; 